import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchAdminProducts,
  deleteAdminProduct,
  uploadProductsExcel,
  executeProductsImport,
  updateAdminProduct,
  createAdminMapping,
  deleteAdminMapping,
  fetchAdminSkills,
  fetchAdminCategories,
  fetchAdminAgeGroups,
  syncProductsFromSalla,
} from "@/admin/api";
import type {
  AdminProduct,
  ColumnMapping,
  ImportParseResponse,
  ImportResult,
  AdminSkill,
  AdminCategory,
  AdminAgeGroup,
} from "@/admin/types";
import Pagination from "@/components/feature/Pagination";
import Modal from "@/components/ui/Modal";
import AdminListCard from "@/pages/admin/components/AdminListCard";

type ImportStep = "idle" | "uploading" | "mapping" | "preview" | "importing" | "done";


export default function AdminProducts() {
  const { t, i18n } = useTranslation("common");
  const isRtl = i18n.language === "ar";

  // Products list state with pagination
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [limit, setLimit] = useState(30);

  // Search and Sort state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Import state
  const [importStep, setImportStep] = useState<ImportStep>("idle");
  const [parseResponse, setParseResponse] = useState<ImportParseResponse | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [uploadedFilePath, setUploadedFilePath] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [successMessage, setSuccessMessage] = useState("");
  const [syncingSalla, setSyncingSalla] = useState(false);
  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => {
      setSuccessMessage("");
    }, 4000);
  };

  // Edit Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<AdminProduct | null>(null);
  const [editForm, setEditForm] = useState({
    sallaProductId: "",
    sku: "",
    price: "",
    imageUrl: "",
    stockQuantity: 0,
    nameAr: "",
    nameEn: "",
  });
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editError, setEditError] = useState("");

  // Link Modal state
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkingProduct, setLinkingProduct] = useState<AdminProduct | null>(null);
  const [selectedSkillId, setSelectedSkillId] = useState("");
  const [skills, setSkills] = useState<AdminSkill[]>([]);
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [ageGroups, setAgeGroups] = useState<AdminAgeGroup[]>([]);
  const [skillSearch, setSkillSearch] = useState("");
  const [isSkillDropdownOpen, setIsSkillDropdownOpen] = useState(false);
  const [isSavingLink, setIsSavingLink] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [metadataLoaded, setMetadataLoaded] = useState(false);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [linkedMappings, setLinkedMappings] = useState<any[]>([]);

  const skillDropdownRef = useRef<HTMLDivElement>(null);

  // Click outside to close skill dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (skillDropdownRef.current && !skillDropdownRef.current.contains(event.target as Node)) {
        setIsSkillDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Locales helpers
  const currentLanguage = useMemo(() => (i18n.language || "ar").split("-")[0], [i18n.language]);
  const defaultLanguage = useMemo(() => {
    const fallback = i18n.options.fallbackLng;
    if (typeof fallback === "string") return fallback;
    if (Array.isArray(fallback)) return fallback[0] || "ar";
    if (fallback && typeof fallback === "object") {
      const entry = Object.values(fallback)[0];
      if (Array.isArray(entry)) return entry[0] || "ar";
      if (typeof entry === "string") return entry;
    }
    return "ar";
  }, [i18n.options.fallbackLng]);

  const getSkillName = (skill: AdminSkill) => {
    const direct = skill.translations?.find((item) => item.locale === currentLanguage)?.title;
    if (direct) return direct;
    const fallback = skill.translations?.find((item) => item.locale === defaultLanguage)?.title;
    if (fallback) return fallback;
    return skill.key;
  };

  const getSkillCategoryName = (skill: AdminSkill) => {
    const cat = categories.find((c) => c.id === skill.category.id);
    if (!cat) return skill.category.key;
    const direct = cat.translations?.find((item) => item.locale === currentLanguage)?.name;
    if (direct) return direct;
    const fallback = cat.translations?.find((item) => item.locale === defaultLanguage)?.name;
    if (fallback) return fallback;
    return cat.key;
  };

  const getSkillAgeGroupLabel = (skill: AdminSkill) => {
    const group = ageGroups.find((g) => g.id === skill.ageGroup.id);
    if (!group) return skill.ageGroup.key;
    const direct = group.translations?.find((item) => item.locale === currentLanguage)?.title;
    if (direct) return direct;
    const fallback = group.translations?.find((item) => item.locale === defaultLanguage)?.title;
    if (fallback) return fallback;
    return group.key;
  };

  const selectedSkill = useMemo(() => {
    return skills.find((s) => s.id === selectedSkillId) || null;
  }, [skills, selectedSkillId]);

  const filteredSkills = useMemo(() => {
    const term = skillSearch.trim().toLowerCase();
    if (!term) return skills;
    const words = term.split(/\s+/);
    return skills.filter((skill) => {
      const skillName = getSkillName(skill).toLowerCase();
      const categoryName = getSkillCategoryName(skill).toLowerCase();
      const ageGroupLabel = getSkillAgeGroupLabel(skill).toLowerCase();
      const optionText = `${categoryName} ${ageGroupLabel} ${skillName}`.toLowerCase();
      return words.every((word) => optionText.includes(word));
    });
  }, [skills, skillSearch, currentLanguage, defaultLanguage, categories, ageGroups]);

  const loadLinkMetadata = async () => {
    if (metadataLoaded) return;
    setLoadingMetadata(true);
    try {
      const [skillsData, categoriesData, ageGroupsData] = await Promise.all([
        fetchAdminSkills(),
        fetchAdminCategories(),
        fetchAdminAgeGroups(),
      ]);
      setSkills(skillsData.filter((s) => s.isActive));
      setCategories(categoriesData.filter((c) => c.isActive));
      setAgeGroups(ageGroupsData.filter((g) => g.isActive));
      setMetadataLoaded(true);
    } catch (err) {
      console.error("Failed to load skills metadata:", err);
    } finally {
      setLoadingMetadata(false);
    }
  };

  const handleOpenEditModal = (product: AdminProduct) => {
    const nameAr = product.translations.find((t) => t.locale === "ar")?.name || "";
    const nameEn = product.translations.find((t) => t.locale === "en")?.name || "";
    setEditingProduct(product);
    setEditForm({
      sallaProductId: product.id,
      sku: product.sku || "",
      price: product.price,
      imageUrl: product.imageUrl,
      stockQuantity: product.stockQuantity,
      nameAr,
      nameEn,
    });
    setEditError("");
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProduct) return;
    
    if (!editForm.sallaProductId.trim() || !editForm.price.trim() || !editForm.imageUrl.trim() || !editForm.nameAr.trim() || !editForm.nameEn.trim()) {
      setEditError(t("adminFillRequired"));
      return;
    }

    setIsSavingEdit(true);
    setEditError("");
    try {
      await updateAdminProduct(editingProduct.id, {
        sallaProductId: editForm.sallaProductId,
        sku: editForm.sku || null,
        price: editForm.price,
        imageUrl: editForm.imageUrl,
        stockQuantity: Number(editForm.stockQuantity),
        nameAr: editForm.nameAr,
        nameEn: editForm.nameEn,
      });
      setIsEditModalOpen(false);
      showSuccess(isRtl ? "تم تعديل المنتج بنجاح" : "Product details updated successfully!");
      loadProducts(currentPage, limit, debouncedSearch, sortBy, sortDirection);
    } catch (err: any) {
      if (err instanceof Error && err.message === "product_id_exists") {
        setEditError(isRtl ? "معرف المنتج موجود بالفعل لمنتج آخر" : "Product ID already exists for another product.");
      } else {
        setEditError(t("adminSaveFailed"));
      }
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleOpenLinkModal = (product: AdminProduct) => {
    setLinkingProduct(product);
    setLinkedMappings(product.mappings || []);
    setSelectedSkillId("");
    setSkillSearch("");
    setLinkError("");
    setIsLinkModalOpen(true);
    loadLinkMetadata();
  };

  const handleAddSkill = async () => {
    if (!linkingProduct || !selectedSkillId) return;

    setIsSavingLink(true);
    setLinkError("");
    try {
      const newMapping = await createAdminMapping({
        sallaProductId: linkingProduct.id,
        skillId: selectedSkillId,
      });
      
      // Update local mappings state
      setLinkedMappings((prev) => [...prev, newMapping]);
      
      // Update products state list so the table updates in real-time
      setProducts((prev) =>
        prev.map((p) =>
          p.id === linkingProduct.id
            ? { ...p, mappings: [...(p.mappings || []), newMapping] }
            : p
        )
      );

      setSelectedSkillId("");
      setSkillSearch("");
    } catch (err: any) {
      setLinkError(isRtl ? "هذا الربط موجود بالفعل!" : "This mapping already exists!");
    } finally {
      setIsSavingLink(false);
    }
  };

  const handleRemoveSkill = async (mappingId: string) => {
    if (!window.confirm(isRtl ? "هل أنت متأكد من إلغاء الربط؟" : "Are you sure you want to unlink?")) return;

    setIsSavingLink(true);
    setLinkError("");
    try {
      await deleteAdminMapping(mappingId);
      
      // Update local mappings state
      setLinkedMappings((prev) => prev.filter((m) => m.id !== mappingId));
      
      // Update products state list
      setProducts((prev) =>
        prev.map((p) =>
          p.id === linkingProduct?.id
            ? { ...p, mappings: (p.mappings || []).filter((m) => m.id !== mappingId) }
            : p
        )
      );
    } catch (err) {
      setLinkError(isRtl ? "تعذر إلغاء الربط" : "Failed to unlink skill");
    } finally {
      setIsSavingLink(false);
    }
  };

  const loadProducts = useCallback((page: number, size: number, search?: string, sort?: string, sortDir?: "asc" | "desc") => {
    setLoadingProducts(true);
    fetchAdminProducts(page, size, search, sort, sortDir)
      .then((res) => {
        setProducts(res.data);
        setTotalPages(res.meta.totalPages);
        setTotalProducts(res.meta.total);
      })
      .catch(() => {
        setProducts([]);
        setTotalPages(1);
        setTotalProducts(0);
      })
      .finally(() => setLoadingProducts(false));
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    loadProducts(currentPage, limit, debouncedSearch, sortBy, sortDirection);
  }, [loadProducts, currentPage, limit, debouncedSearch, sortBy, sortDirection]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortBy, sortDirection, limit]);

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDirection("asc");
    }
  };

  const handleSyncFromSalla = async () => {
    setSyncingSalla(true);
    setImportError(null);
    try {
      const result = await syncProductsFromSalla({ markMissingOutOfStock: false });
      showSuccess(
        isRtl
          ? `تم تحديث ${result.upserted} منتج من المتجر`
          : `Synced ${result.upserted} products from the store`,
      );
      loadProducts(currentPage, limit, debouncedSearch, sortBy, sortDirection);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "salla_sync_failed");
    } finally {
      setSyncingSalla(false);
    }
  };

  const getSortIndicator = (key: string) => {
    if (sortBy !== key) return "↕";
    return sortDirection === "asc" ? "▲" : "▼";
  };

  const getDbLabel = (col: string) => {
    switch (col) {
      case "sallaProductId": return t("dbColSallaProductId");
      case "sku": return t("dbColSku");
      case "price": return t("dbColPrice");
      case "imageUrl": return t("dbColImageUrl");
      case "stockQuantity": return t("dbColStockQuantity");
      case "nameAr": return t("dbColNameAr");
      case "nameEn": return t("dbColNameEn");
      default: return col;
    }
  };

  // ── Handle file upload ──
  const handleFileSelectFixed = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportStep("uploading");
    setImportError(null);
    setImportResult(null);

    try {
      // Upload and parse
      const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api/v1";
      const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
      const url = `${normalizedBaseUrl}/admin/products/import/parse`;
      const formData = new FormData();
      formData.append("file", file);

      const token = localStorage.getItem("admin_token") || sessionStorage.getItem("admin_token_session") || "";
      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || "upload_failed");
      }

      const payload = await response.json();
      const result = payload.data as ImportParseResponse & { filePath?: string };
      setParseResponse(result);
      setMapping(result.mapping);
      setUploadedFilePath(result.filePath || null);
      setImportStep("mapping");
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "upload_failed");
      setImportStep("idle");
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Update column mapping ──
  const updateMapping = (excelColumn: string, dbColumn: string) => {
    setMapping((prev) =>
      prev.map((m) => {
        if (m.excelColumn === excelColumn) return { ...m, dbColumn };
        // If another column was mapped to this dbColumn, unmap it
        if (dbColumn && m.dbColumn === dbColumn) return { ...m, dbColumn: "" };
        return m;
      }),
    );
  };

  // ── Check if required columns are mapped ──
  const isMappingValid = () => {
    const hasId = mapping.some((m) => m.dbColumn === "sallaProductId" && m.excelColumn);
    const hasPrice = mapping.some((m) => m.dbColumn === "price" && m.excelColumn);
    return hasId && hasPrice;
  };

  // ── Preview mapped data ──
  const getMappedPreview = () => {
    if (!parseResponse) return [];
    return parseResponse.previewRows.map((row) => {
      const mapped: Record<string, string> = {};
      for (const m of mapping) {
        if (m.dbColumn) {
          mapped[m.dbColumn] = row[m.excelColumn] ?? "";
        }
      }
      return mapped;
    });
  };

  // ── Execute import ──
  const handleImport = async () => {
    if (!parseResponse) return;

    setImportStep("importing");
    setImportError(null);

    try {
      const result = await executeProductsImport(uploadedFilePath || "", mapping);
      setImportResult(result);
      setImportStep("done");
      // Reload page 1 after import
      if (currentPage === 1) {
        loadProducts(1, limit, debouncedSearch, sortBy, sortDirection);
      } else {
        setCurrentPage(1);
      }
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "import_failed");
      setImportStep("mapping");
    }
  };

  // ── Delete product ──
  const handleDelete = async (id: string) => {
    if (!window.confirm(isRtl ? "هل أنت متأكد من الحذف؟" : "Are you sure?")) return;
    try {
      await deleteAdminProduct(id);
      loadProducts(currentPage, limit, debouncedSearch, sortBy, sortDirection);
    } catch {
      // silent fail
    }
  };

  // ── Reset import ──
  const resetImport = () => {
    setImportStep("idle");
    setParseResponse(null);
    setMapping([]);
    setImportResult(null);
    setImportError(null);
    setUploadedFilePath(null);
  };

  const mappedCount = mapping.filter((m) => m.dbColumn).length;
  const totalColumns = mapping.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">{t("adminProducts")}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {isRtl ? `${totalProducts} منتج مسجل` : `${totalProducts} products registered`}
          </p>
        </div>

        {importStep === "idle" && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSyncFromSalla}
              disabled={syncingSalla}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-teal-200 hover:border-teal-500 text-teal-700 rounded-xl transition-all shadow-sm hover:shadow-md text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {syncingSalla ? (
                <svg className="animate-spin h-4 w-4 text-teal-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <i className="ri-refresh-line" />
              )}
              {isRtl ? "مزامنة من المتجر" : "Sync from store"}
            </button>

          <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-l from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-xl cursor-pointer transition-all shadow-md hover:shadow-lg text-sm font-semibold">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            {isRtl ? "استيراد من Excel" : "Import from Excel"}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleFileSelectFixed}
            />
          </label>
          </div>
        )}
      </div>

      {/* Success Banner */}
      {successMessage && (
        <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl text-sm text-emerald-600 flex items-center gap-2">
          <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{successMessage}</span>
        </div>
      )}

      {/* Import Error */}
      {importError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm text-red-700">{importError}</span>
          <button onClick={() => setImportError(null)} className="mr-auto text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Uploading Spinner */}
      {importStep === "uploading" && (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <div className="animate-spin w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600 font-medium">{isRtl ? "جاري قراءة الملف..." : "Reading file..."}</p>
        </div>
      )}

      {/* Step: Column Mapping */}
      {(importStep === "mapping" || importStep === "preview") && parseResponse && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          {/* Stepper Header */}
          <div className="bg-gradient-to-l from-teal-50 to-emerald-50 px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800">
                  {importStep === "mapping"
                    ? (isRtl ? "الخطوة 1: ربط الأعمدة" : "Step 1: Map Columns")
                    : (isRtl ? "الخطوة 2: معاينة البيانات" : "Step 2: Preview Data")}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {isRtl
                    ? `${parseResponse.totalRows} صف في الملف · ${mappedCount}/${totalColumns} عمود مربوط`
                    : `${parseResponse.totalRows} rows · ${mappedCount}/${totalColumns} columns mapped`}
                </p>
              </div>
              <button
                onClick={resetImport}
                className="text-sm text-gray-500 hover:text-red-500 transition-colors"
              >
                {isRtl ? "إلغاء" : "Cancel"}
              </button>
            </div>

            {/* Step indicators */}
            <div className="flex items-center gap-2 mt-3">
              {["mapping", "preview"].map((step, i) => (
                <div key={step} className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    importStep === step
                      ? "bg-teal-500 text-white shadow-md"
                      : i === 0 && importStep === "preview"
                        ? "bg-teal-100 text-teal-600"
                        : "bg-gray-200 text-gray-500"
                  }`}>
                    {i === 0 && importStep === "preview" ? "✓" : i + 1}
                  </div>
                  {i < 1 && <div className={`w-12 h-0.5 ${importStep === "preview" ? "bg-teal-300" : "bg-gray-200"}`} />}
                </div>
              ))}
            </div>
          </div>

          {/* Mapping Table */}
          {importStep === "mapping" && (
            <div className="p-6">
              <div className="space-y-3">
                {mapping.map((entry) => {
                  const isMapped = !!entry.dbColumn;
                  return (
                    <div key={entry.excelColumn} className={`flex items-center gap-4 p-3 rounded-xl border transition-all ${
                      isMapped ? "border-teal-200 bg-teal-50/50" : "border-gray-200 bg-gray-50/50"
                    }`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isMapped ? "bg-teal-100 text-teal-600" : "bg-gray-200 text-gray-400"
                      }`}>
                        {isMapped ? (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold text-gray-800 truncate block">{entry.excelColumn}</span>
                        <span className="text-xs text-gray-400">{isRtl ? "عمود الإكسل" : "Excel Column"}</span>
                      </div>

                      <svg className="w-5 h-5 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRtl ? "M19 12H5m7-7l-7 7 7 7" : "M5 12h14m-7-7l7 7-7 7"} />
                      </svg>

                      <select
                        value={entry.dbColumn}
                        onChange={(e) => updateMapping(entry.excelColumn, e.target.value)}
                        className={`w-56 px-4 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-teal-200 focus:border-teal-500 outline-none transition-all ${
                          isMapped ? "border-teal-300 bg-white" : "border-gray-200 bg-white"
                        }`}
                      >
                        <option value="">{isRtl ? "— لا يوجد ربط —" : "— Not mapped —"}</option>
                        {parseResponse.dbColumns.map((col) => (
                          <option key={col} value={col} disabled={mapping.some((m) => m.dbColumn === col && m.excelColumn !== entry.excelColumn)}>
                            {getDbLabel(col)}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>

              {!isMappingValid() && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2 text-sm text-amber-700">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.068 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  {isRtl
                    ? "يجب ربط عمودي «معرف المنتج» و «السعر» على الأقل"
                    : "You must map at least 'Product ID' and 'Price' columns"}
                </div>
              )}

              <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100">
                <button
                  onClick={() => setImportStep("preview")}
                  disabled={!isMappingValid()}
                  className="px-6 py-2.5 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl text-sm font-semibold transition-all shadow-sm"
                >
                  {isRtl ? "معاينة البيانات →" : "Preview Data →"}
                </button>
                <button onClick={resetImport} className="px-4 py-2.5 text-gray-500 hover:text-gray-700 text-sm">
                  {isRtl ? "إلغاء" : "Cancel"}
                </button>
              </div>
            </div>
          )}

          {/* Preview Table */}
          {importStep === "preview" && (
            <div className="p-6">
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 border-b">#</th>
                      {mapping.filter((m) => m.dbColumn).map((m) => (
                        <th key={m.dbColumn} className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 border-b whitespace-nowrap">
                          {getDbLabel(m.dbColumn)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {getMappedPreview().map((row, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="px-3 py-2.5 text-gray-400 text-xs">{i + 1}</td>
                        {mapping.filter((m) => m.dbColumn).map((m) => (
                          <td key={m.dbColumn} className="px-3 py-2.5 text-gray-700 max-w-[200px] truncate">
                            {row[m.dbColumn] || <span className="text-gray-300">—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-xs text-gray-400 mt-3">
                {isRtl
                  ? `عرض أول ${Math.min(5, parseResponse.totalRows)} صف من ${parseResponse.totalRows} صف`
                  : `Showing first ${Math.min(5, parseResponse.totalRows)} of ${parseResponse.totalRows} rows`}
              </p>

              <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100">
                <button
                  onClick={handleImport}
                  className="px-6 py-2.5 bg-gradient-to-l from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white rounded-xl text-sm font-bold transition-all shadow-md hover:shadow-lg"
                >
                  {isRtl
                    ? `استيراد ${parseResponse.totalRows} منتج`
                    : `Import ${parseResponse.totalRows} products`}
                </button>
                <button onClick={() => setImportStep("mapping")} className="px-4 py-2.5 text-gray-500 hover:text-gray-700 text-sm">
                  {isRtl ? "← العودة للربط" : "← Back to mapping"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Importing Spinner */}
      {importStep === "importing" && (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <div className="animate-spin w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600 font-medium">{isRtl ? "جاري استيراد المنتجات..." : "Importing products..."}</p>
        </div>
      )}

      {/* Import Result */}
      {importStep === "done" && importResult && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="bg-gradient-to-l from-emerald-50 to-green-50 px-6 py-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-800">{isRtl ? "اكتمل الاستيراد!" : "Import Complete!"}</h3>
                <p className="text-sm text-gray-500">
                  {isRtl
                    ? `تم استيراد ${importResult.imported} منتج · تم تخطي ${importResult.skipped}`
                    : `${importResult.imported} imported · ${importResult.skipped} skipped`}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-emerald-50 rounded-xl">
              <p className="text-2xl font-bold text-emerald-600">{importResult.imported}</p>
              <p className="text-xs text-gray-500 mt-1">{isRtl ? "تم الاستيراد" : "Imported"}</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-xl">
              <p className="text-2xl font-bold text-amber-600">{importResult.skipped}</p>
              <p className="text-xs text-gray-500 mt-1">{isRtl ? "تم التخطي" : "Skipped"}</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-xl">
              <p className="text-2xl font-bold text-red-600">{importResult.errors.length}</p>
              <p className="text-xs text-gray-500 mt-1">{isRtl ? "أخطاء" : "Errors"}</p>
            </div>
          </div>

          {importResult.errors.length > 0 && (
            <div className="px-6 pb-4">
              <details className="group">
                <summary className="text-sm text-red-600 cursor-pointer hover:underline">
                  {isRtl ? "عرض الأخطاء" : "Show errors"}
                </summary>
                <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {importResult.errors.map((err, i) => (
                    <li key={i} className="text-xs text-red-500 bg-red-50 rounded px-3 py-1.5">{err}</li>
                  ))}
                </ul>
              </details>
            </div>
          )}

          <div className="px-6 pb-6">
            <button onClick={resetImport} className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-all">
              {isRtl ? "إغلاق" : "Close"}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-2">{isRtl ? "بحث المنتجات (اسم، SKU، معرف)" : "Search Products (Name, SKU, ID)"}</label>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={isRtl ? "ابحث هنا..." : "Search here..."}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
            />
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <h3 className="font-semibold text-gray-700">{isRtl ? "المنتجات المسجلة" : "Registered Products"}</h3>
        </div>

        {loadingProducts ? (
          <div className="p-12 text-center">
            <div className="animate-spin w-8 h-8 border-3 border-gray-300 border-t-teal-500 rounded-full mx-auto" />
          </div>
        ) : products.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="w-16 h-16 text-gray-200 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <p className="text-gray-400">{isRtl ? "لا توجد منتجات بعد" : "No products yet"}</p>
            <p className="text-xs text-gray-300 mt-1">{isRtl ? "قم باستيراد منتجات من ملف Excel" : "Import products from an Excel file"}</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">
                      <button onClick={() => handleSort("sallaProductId")} className="flex items-center gap-1 font-semibold text-gray-500">
                        <span>{t("adminProductId")}</span>
                        <span className="text-[10px]">{getSortIndicator("sallaProductId")}</span>
                      </button>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">
                      <button onClick={() => handleSort("sku")} className="flex items-center gap-1 font-semibold text-gray-500">
                        <span>SKU</span>
                        <span className="text-[10px]">{getSortIndicator("sku")}</span>
                      </button>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">{isRtl ? "الصورة" : "Image"}</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">{isRtl ? "الاسم" : "Name"}</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">
                      <button onClick={() => handleSort("price")} className="flex items-center gap-1 font-semibold text-gray-500">
                        <span>{t("adminProductPrice")}</span>
                        <span className="text-[10px]">{getSortIndicator("price")}</span>
                      </button>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">
                      <button onClick={() => handleSort("stockQuantity")} className="flex items-center gap-1 font-semibold text-gray-500">
                        <span>{t("adminProductStock")}</span>
                        <span className="text-[10px]">{getSortIndicator("stockQuantity")}</span>
                      </button>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">{t("adminActions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => {
                    const name = product.translations.find((t) => t.locale === i18n.language)?.name
                      || product.translations[0]?.name
                      || product.id;
                    return (
                      <tr key={product.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3">
                          <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-600">{product.id}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-500">{product.sku || "—"}</span>
                        </td>
                        <td className="px-4 py-3">
                          {product.imageUrl ? (
                            <img src={product.imageUrl} alt={name} className="w-10 h-10 rounded-xl object-cover border border-gray-200" />
                          ) : (
                            <div className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center">
                              <svg className="w-5 h-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700 font-medium max-w-[200px] truncate">{name}</td>
                        <td className="px-4 py-3 text-gray-700">{product.price} {t("sar")}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            product.stockQuantity > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                          }`}>
                            {product.stockQuantity}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {/* Link Product Button */}
                            <button
                              onClick={() => handleOpenLinkModal(product)}
                              className={`transition-colors p-1.5 rounded-lg ${
                                product.mappings && product.mappings.length > 0
                                  ? "text-emerald-600 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100/80"
                                  : "text-gray-400 hover:text-teal-600 hover:bg-teal-50"
                              }`}
                              title={
                                isRtl 
                                  ? `ربط بالمهارات (${product.mappings?.length || 0} مرتبط)` 
                                  : `Link to Skills (${product.mappings?.length || 0} linked)`
                              }
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                            </button>

                            {/* Edit Details Button */}
                            <button
                              onClick={() => handleOpenEditModal(product)}
                              className="text-indigo-500 hover:text-indigo-700 transition-colors p-1.5 rounded-lg hover:bg-indigo-50"
                              title={isRtl ? "تعديل" : "Edit"}
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>

                            {/* Delete Button */}
                            <button
                              onClick={() => handleDelete(product.id)}
                              className="text-red-400 hover:text-red-600 transition-colors p-1.5 rounded-lg hover:bg-red-50"
                              title={isRtl ? "حذف" : "Delete"}
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="block md:hidden p-4 space-y-4">
              {products.map((product) => {
                const name = product.translations.find((t) => t.locale === i18n.language)?.name
                  || product.translations[0]?.name
                  || product.id;
                return (
                  <AdminListCard
                    key={product.id}
                    title={name}
                    iconUrl={product.imageUrl || undefined}
                    iconFallback="ri-shopping-bag-3-line"
                    onEdit={() => handleOpenEditModal(product)}
                    badges={[
                      { label: `ID: ${product.id}`, icon: "ri-hashtag" },
                      ...(product.sku ? [{ label: `SKU: ${product.sku}`, icon: "ri-barcode-line" }] : []),
                      { label: `${product.price} ${t("sar")}`, icon: "ri-money-dollar-circle-line" },
                      {
                        label: `${product.stockQuantity}`,
                        icon: product.stockQuantity > 0 ? "ri-checkbox-circle-line" : "ri-close-circle-line",
                      },
                    ]}
                    footerLeft={
                      product.mappings && product.mappings.length > 0 ? (
                        <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg">
                          {product.mappings.length} {isRtl ? "مهارات مرتبطة" : "linked skills"}
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-400">
                          {isRtl ? "غير مرتبط" : "No links"}
                        </span>
                      )
                    }
                    customActions={
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleOpenLinkModal(product)}
                          className={`p-2 rounded-xl border transition-colors ${
                            product.mappings && product.mappings.length > 0
                              ? "text-emerald-600 bg-emerald-50 border-emerald-100 hover:bg-emerald-100"
                              : "text-gray-400 border-gray-100 hover:text-teal-600 hover:bg-teal-50"
                          }`}
                        >
                          <i className="ri-link text-lg" />
                        </button>
                        <button
                          onClick={() => handleOpenEditModal(product)}
                          className="p-2 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 rounded-xl border border-gray-100 transition-colors"
                        >
                          <i className="ri-edit-line text-lg" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl border border-gray-100 transition-colors"
                        >
                          <i className="ri-delete-bin-line text-lg" />
                        </button>
                      </div>
                    }
                  />
                );
              })}
            </div>

            {/* Pagination Controls */}
            <div className="border-t border-gray-100">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={limit}
                onPageChange={setCurrentPage}
                onPageSizeChange={setLimit}
                pageSizeOptions={[10, 20, 50, 100]}
              />
            </div>
          </>
        )}
      </div>

      {/* Edit Product Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title={isRtl ? "تعديل بيانات المنتج" : "Edit Product Details"}
        maxWidth="lg"
        footer={
          <>
            <button
              type="button"
              onClick={() => setIsEditModalOpen(false)}
              className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-xl transition-colors"
            >
              {t("cancel")}
            </button>
            <button
              onClick={handleEditSubmit}
              disabled={isSavingEdit}
              className="px-5 py-2 rounded-xl bg-gradient-to-l from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-sm transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {isSavingEdit && (
                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {t("save")}
            </button>
          </>
        }
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          {editError && (
            <div className="p-3.5 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600 flex items-center gap-2">
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{editError}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Store Product ID */}
            <div className="space-y-1">
              <label className="block text-xs font-bold text-gray-700">
                {t("dbColSallaProductId")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editForm.sallaProductId}
                onChange={(e) => setEditForm({ ...editForm, sallaProductId: e.target.value })}
                className="w-full px-3.5 py-2 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 text-sm bg-white"
              />
              <p className="text-[10px] text-amber-600 font-medium leading-normal">
                {isRtl 
                  ? "تنبيه: تعديل معرف المتجر سينقل جميع العلاقات والترجمات تلقائياً للمعرف الجديد."
                  : "Note: Changing store ID will automatically migrate all mappings and translations."}
              </p>
            </div>

            {/* SKU */}
            <div className="space-y-1">
              <label className="block text-xs font-bold text-gray-700">
                {t("dbColSku")}
              </label>
              <input
                type="text"
                value={editForm.sku}
                onChange={(e) => setEditForm({ ...editForm, sku: e.target.value })}
                className="w-full px-3.5 py-2 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 text-sm bg-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Arabic Name */}
            <div className="space-y-1">
              <label className="block text-xs font-bold text-gray-700">
                {t("dbColNameAr")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editForm.nameAr}
                onChange={(e) => setEditForm({ ...editForm, nameAr: e.target.value })}
                dir="rtl"
                className="w-full px-3.5 py-2 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 text-sm bg-white"
              />
            </div>

            {/* English Name */}
            <div className="space-y-1">
              <label className="block text-xs font-bold text-gray-700">
                {t("dbColNameEn")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editForm.nameEn}
                onChange={(e) => setEditForm({ ...editForm, nameEn: e.target.value })}
                dir="ltr"
                className="w-full px-3.5 py-2 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 text-sm bg-white"
              />
            </div>
          </div>

          {/* Image URL */}
          <div className="space-y-1">
            <label className="block text-xs font-bold text-gray-700">
              {t("dbColImageUrl")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={editForm.imageUrl}
              onChange={(e) => setEditForm({ ...editForm, imageUrl: e.target.value })}
              dir="ltr"
              className="w-full px-3.5 py-2 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 text-sm bg-white"
            />
            {editForm.imageUrl && (
              <div className="mt-2">
                <img
                  src={editForm.imageUrl}
                  alt="Preview"
                  className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                  onError={(e) => {
                    (e.target as HTMLElement).style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Price */}
            <div className="space-y-1">
              <label className="block text-xs font-bold text-gray-700">
                {t("dbColPrice")} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={editForm.price}
                  onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                  className="w-full px-3.5 py-2 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 text-sm bg-white"
                />
                <span className={`absolute top-1/2 -translate-y-1/2 text-xs text-gray-400 font-semibold ${isRtl ? "left-3" : "right-3"}`}>
                  {t("sar")}
                </span>
              </div>
            </div>

            {/* Stock Quantity */}
            <div className="space-y-1">
              <label className="block text-xs font-bold text-gray-700">
                {t("dbColStockQuantity")} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                value={editForm.stockQuantity}
                onChange={(e) => setEditForm({ ...editForm, stockQuantity: Number(e.target.value) })}
                className="w-full px-3.5 py-2 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 text-sm bg-white"
              />
            </div>
          </div>
        </form>
      </Modal>

      {/* Link Product Modal */}
      <Modal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        title={isRtl ? "إدارة ربط مهارات المنتج" : "Manage Product Skill Mappings"}
        maxWidth="2xl"
        footer={
          <button
            type="button"
            onClick={() => setIsLinkModalOpen(false)}
            className="px-5 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold transition-colors"
          >
            {isRtl ? "إغلاق" : "Close"}
          </button>
        }
      >
        <div className="space-y-5">
          {linkError && (
            <div className="p-3.5 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600 flex items-center gap-2">
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{linkError}</span>
            </div>
          )}

          {linkingProduct && (
            <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-100 flex items-center gap-3">
              <img
                src={linkingProduct.imageUrl}
                alt=""
                className="w-12 h-12 rounded-xl object-cover border border-gray-200 bg-white"
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-gray-800 truncate">
                  {linkingProduct.translations.find((t) => t.locale === currentLanguage)?.name ||
                    linkingProduct.translations[0]?.name ||
                    linkingProduct.id}
                </p>
                <p className="text-[10px] text-gray-400 font-mono mt-0.5">ID: {linkingProduct.id}</p>
              </div>
            </div>
          )}

          {loadingMetadata ? (
            <div className="py-8 text-center text-xs text-gray-400 flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4 text-teal-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>{t("loading")}</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Add New Skill Section */}
              <div className="space-y-2 p-4 bg-teal-50/10 border border-teal-100/50 rounded-xl">
                <label className="block text-xs font-bold text-gray-700">
                  {isRtl ? "ربط مهارة جديدة" : "Link New Skill"}
                </label>

                {selectedSkill ? (
                  <div className="flex items-center justify-between p-3 border border-teal-100 bg-teal-50/20 rounded-xl">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-xl border border-gray-100 bg-gray-50 flex items-center justify-center shrink-0">
                        {selectedSkill.iconUrl ? (
                          <img
                            src={selectedSkill.iconUrl}
                            alt=""
                            className="w-6 h-6 object-contain"
                          />
                        ) : (
                          <span className="text-xs text-gray-400">💡</span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-xs font-semibold text-gray-800 truncate">
                          {getSkillName(selectedSkill)}
                        </h4>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                            style={{
                              backgroundColor: `${selectedSkill.category.colorHex}15`,
                              color: selectedSkill.category.colorHex,
                            }}
                          >
                            <span
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ backgroundColor: selectedSkill.category.colorHex }}
                            />
                            {getSkillCategoryName(selectedSkill)}
                          </span>
                          <span className="text-[10px] font-semibold text-gray-500">
                            {getSkillAgeGroupLabel(selectedSkill)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={handleAddSkill}
                        disabled={isSavingLink}
                        className="px-4 py-2 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold transition-all shadow-sm flex items-center gap-1"
                      >
                        {isSavingLink && (
                          <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        )}
                        {isRtl ? "ربط الآن" : "Link Now"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedSkillId("");
                          setSkillSearch("");
                        }}
                        className="text-xs font-semibold text-gray-500 hover:text-gray-700 hover:bg-gray-100 px-2.5 py-2 rounded-lg transition-colors"
                      >
                        {t("cancel")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="relative" ref={skillDropdownRef}>
                      <input
                        type="text"
                        placeholder={isRtl ? "ابحث عن مهارة بالاسم، الركن، أو العمر لربطها..." : "Search for a skill by name, category, or age to link..."}
                        value={skillSearch}
                        onChange={(e) => {
                          setSkillSearch(e.target.value);
                          setIsSkillDropdownOpen(true);
                        }}
                        onFocus={() => setIsSkillDropdownOpen(true)}
                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 text-sm bg-white font-medium shadow-sm"
                      />
                      {isSkillDropdownOpen && (
                        <div className="absolute left-0 right-0 mt-1.5 bg-white border border-gray-100 rounded-xl shadow-2xl overflow-hidden z-50 max-h-60 overflow-y-auto divide-y divide-gray-50">
                          {filteredSkills.length === 0 ? (
                            <div className="p-4 text-center text-xs text-gray-400">{t("adminNoResults")}</div>
                          ) : (
                            filteredSkills.map((skill) => {
                              const sName = getSkillName(skill);
                              const cName = getSkillCategoryName(skill);
                              const aLabel = getSkillAgeGroupLabel(skill);
                              return (
                                <button
                                  key={skill.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedSkillId(skill.id);
                                    setIsSkillDropdownOpen(false);
                                  }}
                                  className="w-full text-right p-3 hover:bg-teal-50/5 flex items-center justify-between gap-3 transition-colors text-right"
                                >
                                  <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-8 h-8 rounded-lg border border-gray-100 bg-gray-50 flex items-center justify-center shrink-0">
                                      {skill.iconUrl ? (
                                        <img
                                          src={skill.iconUrl}
                                          alt=""
                                          className="w-5 h-5 object-contain"
                                        />
                                      ) : (
                                        <span className="text-xs text-gray-400">💡</span>
                                      )}
                                    </div>
                                    <div className="min-w-0 text-right">
                                      <p className="text-xs font-semibold text-gray-700 truncate">{sName}</p>
                                      <p className="text-[10px] text-gray-400 mt-0.5">
                                        {cName} | {aLabel}
                                      </p>
                                    </div>
                                  </div>
                                  <span
                                    className="w-2.5 h-2.5 rounded-full shrink-0"
                                    style={{ backgroundColor: skill.category.colorHex }}
                                    title={cName}
                                  />
                                </button>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                    {isSkillDropdownOpen && <div className="h-60" />}
                  </>
                )}
              </div>

              {/* Currently Linked Skills List */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-700 px-1">
                  {isRtl ? `المهارات المرتبطة حالياً (${linkedMappings.length})` : `Currently Linked Skills (${linkedMappings.length})`}
                </h4>

                {linkedMappings.length === 0 ? (
                  <div className="text-center p-8 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-xs text-gray-400">
                    {isRtl ? "لا توجد مهارات مرتبطة بهذا المنتج بعد." : "No skills linked to this product yet."}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {linkedMappings.map((mapping) => {
                      const skillName = mapping.skillTranslations?.find((t: any) => t.locale === currentLanguage)?.title 
                        || mapping.skillTranslations?.[0]?.title 
                        || mapping.skillKey;
                      const categoryName = mapping.categoryTranslations?.find((t: any) => t.locale === currentLanguage)?.name 
                        || mapping.categoryTranslations?.[0]?.name 
                        || mapping.categoryKey;
                      const ageGroupTitle = mapping.ageGroupTranslations?.find((t: any) => t.locale === currentLanguage)?.title 
                        || mapping.ageGroupTranslations?.[0]?.title 
                        || mapping.ageGroupKey;
                      const color = mapping.categoryColorHex || "#E8927C";

                      return (
                        <div key={mapping.id} className="flex items-center justify-between p-3 border border-gray-100 bg-white rounded-xl shadow-sm hover:border-gray-200 transition-all">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0 text-xs">
                              💡
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-gray-700 truncate">{skillName}</p>
                              <div className="flex flex-wrap items-center gap-2 mt-0.5">
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold"
                                  style={{ backgroundColor: `${color}15`, color: color }}
                                >
                                  {categoryName}
                                </span>
                                <span className="text-[9px] text-gray-400 font-semibold">{ageGroupTitle}</span>
                              </div>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleRemoveSkill(mapping.id)}
                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors shrink-0"
                            title={isRtl ? "إلغاء الربط" : "Remove Link"}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
